import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { NoteArticle } from '@/mocks/notes';

interface ArticleContentProps {
  article: NoteArticle;
}

export default function ArticleContent({ article }: ArticleContentProps) {
  const [visible, setVisible] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      const scrollTop = el.scrollTop || document.body.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      setReadProgress(scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={ref} className="bg-[#faf8f5]">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[100] h-0.5 bg-[#ede5d8]">
        <div
          className="h-full bg-[#b08060] transition-all duration-150"
          style={{ width: `${readProgress}%` }}
        ></div>
      </div>

      {/* Article Hero */}
      <div className="relative pt-24 md:pt-32 pb-0">
        <div className="relative h-72 md:h-[420px] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#faf8f5] via-transparent to-transparent"></div>
        </div>
      </div>

      {/* Article Body */}
      <div className={`w-full px-6 md:px-12 lg:px-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="max-w-2xl mx-auto py-12 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-[0.875rem] text-[#a89880] mb-8">
            <Link to="/notes" className="hover:text-[#7a6248] transition-colors cursor-pointer">鳴箏筆記</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#7a6e62]">{article.categoryLabel}</span>
          </nav>

          {/* Category Tag */}
          <span className="inline-block px-3 py-1 rounded-full bg-[#f0e8dc] text-[#7a6248] text-[0.8125rem] font-medium mb-6">
            {article.categoryLabel}
          </span>

          {/* Title */}
          <h1
            className="font-serif-tc text-[#2c2420] leading-[1.3] mb-6"
            style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
          >
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center gap-4 text-[0.9375rem] text-[#a89880] mb-12 pb-8 border-b border-[#ede5d8]">
            <span>{article.publishedAt}</span>
            <span>·</span>
            <span>約 {article.readTime} 分鐘閱讀</span>
          </div>

          {/* Intro */}
          <p className="text-[1.1875rem] leading-[1.9] text-[#4a3c30] mb-12 font-medium">
            {article.content.intro}
          </p>

          {/* Sections */}
          <div className="space-y-12">
            {article.content.sections.map((section, idx) => (
              <div key={idx}>
                <h2
                  className="font-serif-tc text-[#2c2420] leading-[1.4] mb-4"
                  style={{ fontSize: 'clamp(1.25rem, 2vw, 1.5rem)' }}
                >
                  {section.heading}
                </h2>
                <p className="text-[1.1875rem] leading-[1.9] text-[#5a4a3a]">
                  {section.body}
                </p>
              </div>
            ))}
          </div>

          {/* Closing */}
          <div className="mt-14 pt-10 border-t border-[#ede5d8]">
            <p className="text-[1.0625rem] leading-[1.85] text-[#5a4a3a] italic">
              {article.content.closing}
            </p>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-10">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-[#f0e8dc] text-[#7a6e62] text-[0.8125rem]"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Article CTA */}
          <div className="mt-16 p-8 md:p-10 rounded-2xl bg-[#f5ede0] border border-[#d4c4b0]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-5 h-px bg-[#b08060]"></div>
              <span className="text-[0.8125rem] tracking-[0.15em] text-[#b08060] font-medium uppercase">想聊聊？</span>
            </div>
            <h3
              className="font-serif-tc text-[#2c2420] leading-[1.4] mb-4"
              style={{ fontSize: '1.25rem' }}
            >
              如果這篇文章讓你有什麼想法，<br />歡迎加入 LINE@ 告訴我
            </h3>
            <p className="text-[1rem] leading-[1.8] text-[#7a6e62] mb-7">
              不急著決定，也不需要立刻報名。<br />
              先問問，先聊聊你現在的狀態——這就夠了。
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://line.me"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#7a6248] text-white text-[0.9375rem] font-medium hover:bg-[#6a5238] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-line-fill"></i>
                加入 LINE@ 諮詢
              </a>
              <Link
                to="/notes"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#c8b89a] text-[#7a6248] text-[0.9375rem] font-medium hover:bg-[#f0e8dc] transition-all cursor-pointer whitespace-nowrap"
              >
                繼續閱讀其他文章
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
